// ./src/components/generic/ClientSelector/ClientSelector.tsx
import React, { useMemo, useEffect, useRef } from "react";
import { Select, MultiSelect, Text, MultiSelectProps } from "@mantine/core";

import { useClients } from "@/hooks/useClients";
import { Client } from "@/utils/api/ClientService";
import { LoadingIndicator } from "@/components/generic";

export interface ClientSelectorProps {
  /** Selected client ID(s) - string for single select, string[] for multiple */
  value: string | string[] | null;

  /** Callback when client selection changes */
  onChange?: (
    clientId: string | string[] | null,
    client?: Client | Client[]
  ) => void;

  /** Optional array of existing clients (if not provided, will fetch from API) */
  clients?: Client[] | null;

  /** Whether to allow multiple selection */
  multiple?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive clients */
  includeInactive?: boolean;

  /** Custom filter function for clients */
  clientFilter?: (client: Client) => boolean;

  /** Whether the field can be cleared */
  clearable?: boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select/MultiSelect */
  [key: string]: any;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  clients: providedClients,
  multiple = false,
  placeholder = "Select a client",
  includeInactive = false,
  clientFilter,
  clearable = true,
  required = false,
  loadingPlaceholder = "Loading clients...",
  emptyText = "No clients available",
  error,
  disabled,
  size = "sm",
  ...selectProps
}) => {
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialLoadRef = useRef<boolean>(false);

  // Use clients hook only if no clients provided
  const shouldUseHook = !providedClients;
  const clientsHook = useClients({
    autoLoad: false,
  });

  // Determine which clients to use
  const effectiveClients = shouldUseHook
    ? clientsHook.clients
    : providedClients || [];
  const loading = shouldUseHook ? clientsHook.loading : false;
  const lastError = shouldUseHook ? clientsHook.lastError : null;
  const isEmpty = effectiveClients.length === 0;

  // Initial load effect - runs only once on mount if using hook
  useEffect(() => {
    if (shouldUseHook && !hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;

      // Prepare the filter - include existing value(s) with alongWithId
      const existingIds: string[] = [];
      if (value) {
        if (typeof value === "string") {
          existingIds.push(value);
        } else if (Array.isArray(value)) {
          existingIds.push(...value.filter(Boolean));
        }
      }

      const filters = {
        alongWithId: existingIds.length > 0 ? existingIds : undefined,
      };

      // Load initial data (first N clients + any specified by ID)
      clientsHook.list(filters, { page: 1, pageSize: 50 }, undefined, true);
    }
  }, [shouldUseHook]); // Only depend on shouldUseHook

  // Calculate dimensions based on size
  const dimensions = useMemo(() => {
    const sizeMap = {
      xs: { loaderSize: 16 },
      sm: { loaderSize: 18 },
      md: { loaderSize: 20 },
      lg: { loaderSize: 24 },
      xl: { loaderSize: 28 },
    };
    return sizeMap[size as keyof typeof sizeMap] || sizeMap.sm;
  }, [size]);

  // Helper function to format client display name
  const getClientDisplayName = (client: Client): string => {
    if (client.companyName) {
      const personName = [client.nameTitle, client.firstName, client.lastName]
        .filter(Boolean)
        .join(" ");
      return personName
        ? `${client.companyName} (${personName})`
        : client.companyName;
    } else {
      const personName = [client.nameTitle, client.firstName, client.lastName]
        .filter(Boolean)
        .join(" ");
      return personName || "Unnamed Client";
    }
  };

  // Filter and prepare client options
  const clientOptions = useMemo(() => {
    let filteredClients = effectiveClients;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredClients = filteredClients.filter(
        (client) => client.status === 100 // Only show active clients
      );
    }

    // Apply custom filter if provided
    if (clientFilter) {
      filteredClients = filteredClients.filter(clientFilter);
    }

    // Convert to select options
    return filteredClients.map((client) => ({
      value: client.id || "",
      label: getClientDisplayName(client),
      disabled: client.status === 0, // Disabled clients have status 0
    }));
  }, [effectiveClients, includeInactive, clientFilter]);

  // Create a map for quick client lookup by ID
  const clientMap = useMemo(() => {
    const map = new Map();
    effectiveClients.forEach((client) => {
      if (client.id) {
        map.set(client.id, client);
      }
    });
    return map;
  }, [effectiveClients]);

  // Auto-select first client if required and no value provided
  useEffect(() => {
    if (required && clientOptions.length > 0 && !loading && onChange) {
      // For single select mode
      if (!multiple && !value) {
        const firstClient = clientOptions[0];
        if (firstClient && !firstClient.disabled) {
          const clientObject = clientMap.get(firstClient.value);
          onChange(firstClient.value, clientObject);
        }
      }
      // For multiple select mode
      else if (
        multiple &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        const firstClient = clientOptions[0];
        if (firstClient && !firstClient.disabled) {
          const clientObject = clientMap.get(firstClient.value);
          onChange([firstClient.value], [clientObject]);
        }
      }
    }
  }, [required, value, clientOptions, loading, onChange, clientMap, multiple]);

  // Handle search with debouncing (only if using hook and in search mode)
  const handleSearch = (searchValue: string) => {
    if (!shouldUseHook || loading) return;

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search is empty, reset to initial state
    if (!searchValue.trim()) {
      // Reload initial data with alongWithId if we have a value
      const existingIds: string[] = [];
      if (value) {
        if (typeof value === "string") {
          existingIds.push(value);
        } else if (Array.isArray(value)) {
          existingIds.push(...value.filter(Boolean));
        }
      }

      const filters = {
        alongWithId: existingIds.length > 0 ? existingIds : undefined,
      };

      clientsHook.list(filters, { page: 1, pageSize: 50 }, undefined, false);
      return;
    }

    // Set search mode and perform search

    searchTimeoutRef.current = setTimeout(() => {
      const filters = {
        searchKeyword: searchValue,
      };

      console.log("list: ", filters);

      clientsHook.list(filters, { page: 1, pageSize: 50 }, undefined, false);
    }, 500); // 500ms delay for search
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle onChange with client object lookup
  const handleChange = (selectedValue: string | string[] | null) => {
    if (!multiple) {
      // Single select mode
      const selectedClientId = selectedValue as string | null;
      if (selectedClientId) {
        const selectedClient = clientMap.get(selectedClientId);
        onChange?.(selectedClientId, selectedClient);
      } else {
        // When clearing selection, pass null for both ID and client
        onChange?.(null, undefined);
      }
    } else {
      // Multiple select mode
      const selectedClientIds = selectedValue as string[] | null;
      if (selectedClientIds && selectedClientIds.length > 0) {
        const selectedClients = selectedClientIds
          .map((id) => clientMap.get(id))
          .filter(Boolean); // Remove any undefined values
        onChange?.(selectedClientIds, selectedClients);
      } else {
        // When clearing selection, pass empty arrays
        onChange?.([], []);
      }
    }
  };

  // Show loading state using disabled Select/MultiSelect
  if (loading && effectiveClients.length === 0) {
    if (multiple) {
      return (
        <MultiSelect
          {...selectProps}
          size={size}
          data={[]}
          value={[]}
          onChange={() => {}}
          placeholder={loadingPlaceholder}
          disabled
          error={error}
          rightSection={
            <LoadingIndicator size={dimensions.loaderSize} color="gray" />
          }
        />
      );
    }

    return (
      <Select
        {...selectProps}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}}
        placeholder={loadingPlaceholder}
        disabled
        error={error}
        rightSection={
          <LoadingIndicator size={dimensions.loaderSize} color="gray" />
        }
      />
    );
  }

  // Show error state using disabled Select/MultiSelect
  if (lastError && !loading) {
    const errorPlaceholder = "Error loading clients";

    if (multiple) {
      return (
        <MultiSelect
          {...selectProps}
          size={size}
          data={[]}
          value={[]}
          onChange={() => {}}
          placeholder={errorPlaceholder}
          disabled
          error={error || "Failed to load clients"}
        />
      );
    }

    return (
      <Select
        {...selectProps}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}}
        placeholder={errorPlaceholder}
        disabled
        error={error || "Failed to load clients"}
      />
    );
  }

  // Show empty state if no clients are available
  if (isEmpty && !loading) {
    if (multiple) {
      return (
        <MultiSelect
          {...selectProps}
          size={size}
          data={[]}
          value={[]}
          onChange={() => {}}
          placeholder={emptyText}
          disabled
          error={error}
        />
      );
    }

    return (
      <Select
        {...selectProps}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}}
        placeholder={emptyText}
        disabled
        error={error}
      />
    );
  }

  // Normalize value for the select component
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => {
    return <Text size="sm">{option.label}</Text>;
  };

  // Render the appropriate select component
  if (multiple) {
    return (
      <MultiSelect
        {...selectProps}
        size={size}
        data={clientOptions}
        renderOption={renderMultiSelectOption}
        value={normalizedValue as string[]}
        onChange={handleChange}
        placeholder={placeholder}
        clearable={clearable}
        searchable
        onSearchChange={shouldUseHook ? handleSearch : () => {}}
        required={required}
        disabled={disabled || loading}
        error={error}
        // Add loading indicator in rightSection when loading more data
        rightSection={
          loading && effectiveClients.length > 0 ? (
            <LoadingIndicator size={dimensions.loaderSize - 4} color="gray" />
          ) : (
            selectProps.rightSection
          )
        }
      />
    );
  }

  console.log("CP:", { loading, value, clientOptions });

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={clientOptions}
      value={normalizedValue as string | null}
      onChange={handleChange}
      placeholder={placeholder}
      clearable={clearable}
      searchable
      onSearchChange={shouldUseHook ? handleSearch : () => {}}
      required={required}
      disabled={disabled || loading}
      error={error}
      // Add loading indicator in rightSection when loading more data
      rightSection={
        loading && effectiveClients.length > 0 ? (
          <LoadingIndicator size={dimensions.loaderSize - 4} color="gray" />
        ) : (
          selectProps.rightSection
        )
      }
    />
  );
};
